/**
 * HormonalTwinPage — iter628 — Хормонално-репродуктивен близнак (11-ти).
 * Aggregator над cycle + menopause; чете snapshot, не лога нов запис тук.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Flower2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import BreastPainBrief from "@/components/breast/BreastPainBrief";
import ReproductiveAxisCard from "@/components/hormonal/ReproductiveAxisCard";
import ModuleRatingButton from "@/components/ModuleRatingButton";  // iter1114.b
import EndocrineAxesCard from "@/components/hormonal/EndocrineAxesCard";
import DutchMetabolitesCard from "@/components/hormonal/DutchMetabolitesCard";
import CalciumPthCard from "@/components/shared/CalciumPthCard";
import { twinLabel } from "@/lib/twinCatalog";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function HormonalTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [examDate, setExamDate] = useState(new Date().toISOString().slice(0, 10));
  const [logging, setLogging] = useState(null);

  // iter1056 — един бутон: изследването се записва без влизане в BHI
  const logExam = async (modality) => {
    setLogging(modality);
    try {
      await axios.post(`${API}/bhi/screening/exam`, { modality, date: examDate });
      const { data } = await axios.get(`${API}/hormonal-twin/snapshot`);
      setSnap(data);
      toast.success("Записано. Скринингът в оценката е обновен.");
    } catch (e) {
      toast.error("Записът не мина. Опитай пак.");
    } finally {
      setLogging(null);
    }
  };

  useEffect(() => {
    axios.get(`${API}/hormonal-twin/snapshot`)
      .then(({ data }) => setSnap(data))
      .finally(() => setLoading(false));
    const onChange = () => {
      axios.get(`${API}/hormonal-twin/snapshot`).then(({ data }) => setSnap(data)).catch(() => {});
    };
    window.addEventListener("cycle-changed", onChange);
    window.addEventListener("menopause-changed", onChange);
    return () => {
      window.removeEventListener("cycle-changed", onChange);
      window.removeEventListener("menopause-changed", onChange);
    };
  }, [API]);

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="hormonal-twin-page">
      <MobileTopBar title="Хормони" fallbackTo="/dashboard" testid="hormonal-twin-mobile-topbar" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Към таблото
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Flower2 className="w-5 h-5 text-pink-500" />
          <h1 className="text-xl font-bold">{twinLabel("hormonal")}</h1>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Обединява менструалния цикъл, фазата, признаците към и след менопаузата и FSH и E2 от лабораторните изследвания.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-pink-500" /></div>
        ) : (
          <>
            <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="hormonal-snapshot">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold" data-testid="hormonal-score">{snap?.composite ?? "—"}</span>
                <span className="text-xs opacity-60">/100</span>
                {snap?.meno_stage_id && (
                  <span className="ml-auto text-xs px-2 py-1 rounded-md font-bold bg-pink-500 text-white" data-testid="meno-stage">
                    {snap.meno_stage_id}
                  </span>
                )}
              </div>
              <p className="text-sm leading-relaxed mt-2" data-testid="hormonal-interpretation">{snap?.interpretation_bg}</p>
              {snap?.current_phase && (
                <div className={`mt-3 p-3 rounded-lg ${isDark ? "bg-pink-900/20 border border-pink-500/30" : "bg-pink-50 border border-pink-200"}`} data-testid="current-phase">
                  <div className="text-[10px] uppercase font-bold opacity-70 mb-1">Текуща фаза</div>
                  <div className="text-sm font-bold">{snap.current_phase.phase_name_bg} · ден {snap.current_phase.cycle_day}/{snap.current_phase.cycle_length}</div>
                  <div className="text-xs opacity-80 mt-1">{snap.current_phase.description_bg}</div>
                </div>
              )}
              {snap?.sub_scores && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                  {Object.entries(snap.sub_scores).map(([k, v]) => (
                    <div key={k} className={`p-2 rounded-lg text-center ${k === "breast_screening" ? (isDark ? "bg-pink-900/30 border border-pink-500/40" : "bg-pink-50 border border-pink-200") : (isDark ? "bg-slate-800/50" : "bg-slate-100")}`} data-testid={`hormonal-sub-${k}`}>
                      <div className="opacity-60 text-[10px] leading-tight">{snap?.sub_score_labels_bg?.[k] || k}</div>
                      <div className="font-bold">{v ?? "—"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* iter1060 — репродуктивна ос: овулация, препарат, етаж */}
            <div className="mb-4"><ReproductiveAxisCard isDark={isDark} /></div>

            {/* iter1113 — DUTCH 17 метаболита като 4 образователни панела */}
            {snap?.dutch_summary && (
              <DutchMetabolitesCard summary={snap.dutch_summary} isDark={isDark} />
            )}

            {/* iter1059 — сводка от водения модул за болка в гърдата */}
            <div className="mb-4"><BreastPainBrief isDark={isDark} context="hormonal" /></div>

            {/* iter1055 — видим BHI: скринингът на гърда участва в тази оценка */}            {snap?.breast_screening && (
              <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="hormonal-breast-screening">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-4 h-4 text-pink-500" />
                  <h2 className="font-bold text-sm">Скрининг на гърда в тази оценка</h2>
                  {snap.breast_screening.counted_in_composite ? (
                    <span className="ml-auto text-[11px] px-2 py-1 rounded-md font-bold bg-pink-500 text-white" data-testid="breast-screening-weight">
                      участва с {snap.breast_screening.weight_pct}%
                    </span>
                  ) : (
                    <span className="ml-auto text-[11px] px-2 py-1 rounded-md font-bold bg-slate-400 text-white" data-testid="breast-screening-weight">
                      не участва
                    </span>
                  )}
                </div>

                {snap.breast_screening.counted_in_composite ? (
                  <>
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-2xl font-bold" data-testid="breast-screening-score">{snap.breast_screening.score}</span>
                      <span className="text-xs opacity-60">/100</span>
                      <span className="text-xs font-semibold ml-2" data-testid="breast-screening-status">{snap.breast_screening.label_bg}</span>
                    </div>
                    {snap.breast_screening.category_bg && (
                      <div className="text-xs opacity-75 mt-1">Категория риск: {snap.breast_screening.category_bg}</div>
                    )}
                    <div className="mt-3 space-y-1" data-testid="breast-screening-items">
                      {(snap.breast_screening.items || []).map((it, i) => (
                        <div key={i} className={`flex flex-wrap items-center gap-2 text-xs p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}>
                          <span className="flex-1 min-w-[120px]">{it.title_bg}</span>
                          <span className={`font-bold ${it.status === "overdue" ? "text-rose-500" : it.status === "missing" ? "text-amber-500" : "text-emerald-500"}`}>
                            {it.status_bg}
                          </span>
                          {it.loggable && (
                            <button
                              onClick={() => logExam(it.modality_key)}
                              disabled={logging === it.modality_key}
                              className="text-[11px] font-bold px-2 py-1 rounded-md bg-pink-500 text-white disabled:opacity-50 hover:bg-pink-600"
                              data-testid={`log-exam-${it.modality_key}`}
                            >
                              {logging === it.modality_key ? "Записвам…" : "Записах на тази дата"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[11px] opacity-60">Дата на изследването</span>
                      <input
                        type="date"
                        value={examDate}
                        max={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setExamDate(e.target.value)}
                        className={`px-2 py-1 rounded-md border text-xs ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`}
                        data-testid="exam-date-input"
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-xs opacity-80 mt-2" data-testid="breast-screening-reason">{snap.breast_screening.reason_bg}</p>
                )}

                {snap.breast_screening.why_bg && (
                  <p className="text-xs opacity-75 mt-3 leading-relaxed">{snap.breast_screening.why_bg}</p>
                )}
                <button
                  onClick={() => navigate(snap.breast_screening.route || "/health-intelligence/breast")}
                  className="mt-3 text-xs font-bold text-pink-500 hover:underline"
                  data-testid="goto-bhi-screening-btn"
                >
                  {snap.breast_screening.route_label_bg || "Отворете плана за проверките на гърдата →"}
                </button>
              </div>
            )}

            {/* iter1046 — ендокринните оси (Endocrine1) */}
            <EndocrineAxesCard isDark={isDark} />
            {/* iter1053 — същата калциева ос, показана и в бъбречния близнак */}
            <div className="mt-4">
              <CalciumPthCard isDark={isDark} />
            </div>

            <div className="flex gap-2">
              <Button onClick={() => navigate("/dashboard/cycle-aware")} variant="outline" className="flex-1" data-testid="goto-cycle-btn">
                Лог цикъл
              </Button>
              <Button onClick={() => navigate("/dashboard/menopause")} variant="outline" className="flex-1" data-testid="goto-menopause-btn">
                Менопаузален профил
              </Button>
            </div>

            {/* iter1114.b — Rate-per-feature */}
            <ModuleRatingButton moduleKey="hormonal_twin" moduleName="Хормоналния близнак" isDark={isDark} />
          </>
        )}
      </div>
    </div>
  );
}
